import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import { HeaderIconButton } from '@/layouts/main/ui/Header/HeaderIconButton'

import { OmniboxSearch } from './OmniboxSearch'

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent)
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl K'

/** Ctrl+K / ⌘+K opens the search from anywhere in the app. */
function useOpenShortcut(open: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        open()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])
}

/** Looks like a search field in the purple top bar; opens the pop-up. */
function SearchFieldButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={t`Search or ask Elara`}
      aria-haspopup="dialog"
      sx={{
        height: 36,
        width: 280,
        px: 1.5,
        gap: 1,
        justifyContent: 'flex-start',
        borderRadius: '8px',
        color: 'rgba(255,255,255,0.9)',
        backgroundColor: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.35)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
      }}
    >
      <SearchIcon fontSize="small" />
      <Typography noWrap fontSize="0.9rem" sx={{ flex: 1, textAlign: 'left' }}>
        {t`Search or ask Elara…`}
      </Typography>
      <Box component="kbd" sx={{ fontFamily: 'inherit', fontSize: '0.75rem', px: 0.75, borderRadius: '4px', border: '1px solid rgba(255,255,255,0.4)' }}>
        {SHORTCUT_LABEL}
      </Box>
    </ButtonBase>
  )
}

/**
 * Top-bar entry to the dashboard search, on every page of the main layout (consultation screens use
 * their own layout without this bar). Hidden on the dashboard itself, which shows the big search card;
 * the keyboard shortcut works everywhere.
 */
export function TopbarOmnibox(): JSX.Element {
  useLingui()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true })
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const { pathname } = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)
  useOpenShortcut(open)

  // A route change (e.g. a result was picked) always closes the pop-up.
  useEffect(() => setIsOpen(false), [pathname])

  const isDashboard = pathname === '/'
  return (
    <>
      {!isDashboard &&
        (isDesktop ? (
          <SearchFieldButton onClick={open} />
        ) : (
          <Tooltip title={t`Search or ask Elara`}>
            <HeaderIconButton onClick={open} aria-label={t`Search or ask Elara`} aria-haspopup="dialog">
              <SearchIcon />
            </HeaderIconButton>
          </Tooltip>
        ))}
      <Dialog
        open={isOpen}
        onClose={close}
        fullScreen={isPhone}
        fullWidth
        maxWidth="sm"
        aria-label={t`What would you like to do?`}
        PaperProps={{ sx: isPhone ? { p: 2 } : { alignSelf: 'flex-start', mt: '10vh', p: 3, borderRadius: '16px' } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography component="h2" fontWeight={700} fontSize="1.2rem" color="#35374C">
            {t`What would you like to do?`}
          </Typography>
          <IconButton onClick={close} aria-label={t`Close`}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {isOpen && <OmniboxSearch autoFocus inlineResults onDone={close} />}
      </Dialog>
    </>
  )
}
